// The Labels cell's popover body, as the canvas draws it (spec 010 §1.3): a "Labels" title, a field holding
// the selected labels as removable chips with a search input, the remaining labels as chips grouped under
// collapsible category headings (open by default) in a scrolling box, and Cancel / Update. The user picks,
// then confirms, because updateProject({labels}) replaces the whole set and every save refetches the list
// (spec 010 D2). Company labels load on FIRST OPEN, like 008's salesperson list, not in the page query.
//
// Built here rather than on LabelDropdown: the design-system MultiCombobox behind it always shows its list as
// a floating dropdown of rows, which is not what the canvas draws. Labels cannot be created here.
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {graphql} from 'react-relay';
import {Button, EMPHASIS, FlexRow, Icon, Text, WorkSignifier} from '@forecast-it/design-system';
// eslint-disable-next-line import/no-internal-modules -- the chip's own text-colour rule, so the remove button matches it
import {getTextColor} from '@forecast-it/design-system/dist/forecast-v2/shared/colorUtils';
import Styled, {createGlobalStyle, useTheme} from 'styled-components';
import Util from '../../../shared/util/util';
import UpdateProjectMutation from '../../../../mutations/update_project_mutation.modern';
import {useForecastFetchQuery} from '../../../shared/hooks/useForecastFetchQuery';
import type {LabelType} from '../../../../components/dropdowns/LabelDropdown/LabelDropdown';
import type {OpportunityNode} from '../../opportunityTypes';

// The same company-label selection the Create Opportunity modal runs for its Labels field.
const labelOptionsQuery = graphql`
	query LabelsEditor_Query {
		viewer {
			company {
				labels(first: 10000, labelType: PROJECT) {
					edges {
						node {
							id
							name
							color
							category {
								id
								name
							}
						}
					}
				}
			}
		}
	}
`;

type QueryData = {viewer: {company: {labels: {edges: Array<{node: LabelType}>}}}};

export interface LabelsEditorProps {
	node: OpportunityNode;
	// The page's retry() — the list refetches after a save.
	onCommit: () => void;
	// Dismisses the popover.
	close: () => void;
	title: string;
	updateLabel: string;
	cancelLabel: string;
	loadingLabel: string;
	// Shown when the query failed, so a dead popover is distinguishable from a slow one.
	failedLabel: string;
	searchLabel: string;
	noCategoryLabel: string;
	allAppliedLabel: string;
	noMatchLabel: (query: string) => string;
	removeLabel: (name: string) => string;
}

const MAIN = '#232334';
const AUX = '#6b6d7b';
const PRIMARY = '#0004db';
const BORDER = '#e2e2ea';
// The app's top navigation bar (TopNavigation, 57px) plus a small gap. The popover is drawn below it, and Radix
// does not know it is there, so an editor that opens upwards leaves it room itself (QA D-10).
const APP_HEADER_ROOM_PX = 64;
const EDGE_ROOM_PX = 8;

// The floating chat button (ChatIcon's ChatButton, z-index 1000000, 24px from the bottom right) is drawn over
// everything, the design-system popover (z-index 49) included, so near the bottom right of a 1280-1366px window
// it covered most of Update (QA D-11). While this editor is open its popover is drawn just above it; the button
// is back on top as soon as the editor closes. Radix sets the wrapper's z-index inline, hence !important.
const AboveChatButton = createGlobalStyle`
	[data-radix-popper-content-wrapper]:has([data-cy='labels-editor']) {
		z-index: 1000001 !important;
	}
`;
// The smallest editor that still shows the title, the field, a little of the list and the buttons.
const MIN_HEIGHT_PX = 240;

// At full size the editor is ~460px tall, which fits neither above nor below a row in the middle of a short
// window (QA D-10). So it takes at most the height Radix reports free on the side it opened (less the app
// header when that side is the top), and the label list then the field give up height and scroll; the title,
// the search and Cancel / Update always stay in view. Its floor makes Radix open it upwards instead when
// below the row there is less room than that. With room to spare it renders at full size.
const Body = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 360px;
	min-height: ${MIN_HEIGHT_PX}px;
	max-height: calc(var(--radix-popover-content-available-height, 100vh) - ${EDGE_ROOM_PX}px);
	[data-side='top'] > & {
		max-height: calc(var(--radix-popover-content-available-height, 100vh) - ${APP_HEADER_ROOM_PX}px);
	}
	padding: 16px;
	box-sizing: border-box;
`;

const Title = Styled.span`
	flex: none;
	font-size: 16px;
	line-height: 22px;
	font-weight: 500;
	color: ${MAIN};
`;

// The field and the list, sharing whatever height the editor has between the title and the buttons.
const Picker = Styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
`;

const Footer = Styled.div`
	flex: none;
`;

const Field = Styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px;
	flex: 0 1 auto;
	min-height: 36px;
	overflow-y: auto;
	box-sizing: border-box;
	padding: 5px 8px 5px 5px;
	border: 1px solid ${PRIMARY};
	border-radius: 4px;
	background: #fff;
	cursor: text;
`;

// A design-system chip never wraps its text; a label wider than the box wraps inside its chip instead of
// pushing the box wider than the popover.
const wrapChipText = `
	max-width: 100%;
	min-width: 0;
	& > div {
		max-width: 100%;
		min-width: 0;
	}
	& > div > div {
		min-width: 0;
		white-space: normal;
		overflow-wrap: anywhere;
	}
`;

// A selected label: the chip plus its remove button, on the chip's colour.
const SelectedChip = Styled.span<{$color: string}>`
	display: inline-flex;
	align-items: center;
	max-width: 100%;
	min-width: 0;
	border-radius: 4px;
	background: ${({$color}) => $color};
	padding-right: 4px;
	& > div:first-child {
		min-width: 0;
		cursor: default;
	}
	& > div:first-child > div {
		min-width: 0;
		white-space: normal;
		overflow-wrap: anywhere;
	}
`;

const RemoveButton = Styled.button.attrs({type: 'button'})`
	all: unset;
	box-sizing: border-box;
	flex: none;
	width: 18px;
	height: 18px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	cursor: pointer;
	&:focus-visible {
		outline: 2px solid ${PRIMARY};
	}
`;

const SearchInput = Styled.input`
	flex: 1 1 80px;
	min-width: 80px;
	border: none;
	outline: none;
	font-size: 14px;
	line-height: 20px;
	color: ${MAIN};
	padding: 2px 4px;
	background: transparent;
	font-family: inherit;
`;

const Options = Styled.div`
	margin-top: 8px;
	/* Gives up height before the field does, down to about two rows of chips. */
	flex: 0 1000 auto;
	min-height: 72px;
	max-height: 200px;
	overflow-y: auto;
	border: 1px solid ${BORDER};
	border-radius: 4px;
`;

const GroupHeading = Styled.button.attrs({type: 'button'})`
	all: unset;
	box-sizing: border-box;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 12px 4px;
	cursor: pointer;
	font-size: 13px;
	line-height: 18px;
	color: ${AUX};
`;

const GroupItems = Styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 6px;
	padding: 4px 12px 10px;
`;

const OptionChip = Styled.button.attrs({type: 'button'})`
	all: unset;
	cursor: pointer;
	display: flex;
	${wrapChipText}
	&:focus-visible {
		outline: 2px solid ${PRIMARY};
		border-radius: 4px;
	}
`;

const Empty = Styled.div`
	padding: 12px;
	font-size: 13px;
	color: ${AUX};
`;

const NO_CATEGORY = 'no-category';

const idsOf = (node: OpportunityNode): string[] =>
	(node.projectLabels?.edges ?? []).flatMap(e => (e?.node?.label?.id ? [e.node.label.id] : []));

const sameSet = (a: string[], b: string[]): boolean => a.length === b.length && a.every(id => b.includes(id));

const byName = (a: {name?: string | null}, b: {name?: string | null}) => (a.name ?? '').localeCompare(b.name ?? '');

type Group = {key: string; name: string; labels: LabelType[]};

// "No category" first, then the categories by name; labels by name inside each.
const groupsOf = (labels: LabelType[], noCategoryLabel: string): Group[] => {
	const groups = new Map<string, Group>();
	[...labels].sort(byName).forEach(label => {
		const key = label.category?.id ?? NO_CATEGORY;
		const name = label.category?.id ? label.category.name ?? '' : noCategoryLabel;
		if (!groups.has(key)) groups.set(key, {key, name, labels: []});
		groups.get(key)?.labels.push(label);
	});
	return [...groups.values()].sort((a, b) =>
		a.key === NO_CATEGORY ? -1 : b.key === NO_CATEGORY ? 1 : a.name.localeCompare(b.name)
	);
};

/**
 * Radix places the popover for the size it has when it opens, here the short "Loading…" state, and does not
 * place it again when it grows: one opened above its row then grew down over that row, and in a short window
 * off the bottom (QA D-10). Radix does re-place it on a window resize, and the design-system popover offers no
 * other way in, so this announces one whenever the editor's height changes: when the labels load, and when
 * chips or the search change the field or the list. A height change can only follow from new content or the
 * height Radix allows, so this settles after one round.
 */
export const useRepositionOnResize = (ref: React.RefObject<HTMLElement>): void => {
	useEffect(() => {
		const element = ref.current;
		if (!element || typeof ResizeObserver === 'undefined') return undefined;
		let lastHeight = element.getBoundingClientRect().height;
		const observer = new ResizeObserver(() => {
			const height = element.getBoundingClientRect().height;
			if (height === lastHeight) return;
			lastHeight = height;
			window.dispatchEvent(new Event('resize'));
		});
		observer.observe(element);
		return () => observer.disconnect();
	}, [ref]);
};

export const LabelsEditor = ({
	node,
	onCommit,
	close,
	title,
	updateLabel,
	cancelLabel,
	loadingLabel,
	failedLabel,
	searchLabel,
	noCategoryLabel,
	allAppliedLabel,
	noMatchLabel,
	removeLabel,
}: LabelsEditorProps): JSX.Element => {
	const theme = useTheme();
	const {data, fetch, loading} = useForecastFetchQuery<QueryData, Record<string, never>>(labelOptionsQuery);
	// The hook surfaces no error of its own, so track the failure here (same reason as SalespersonEditor).
	const [failed, setFailed] = useState(false);
	const stored = useMemo(() => idsOf(node), [node]);
	const [selected, setSelected] = useState<string[]>(stored);
	const [query, setQuery] = useState('');
	const [collapsed, setCollapsed] = useState<string[]>([]);
	const inputRef = useRef<HTMLInputElement>(null);
	const bodyRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		fetch(undefined, undefined, () => setFailed(true));
		// Mount-only: fetch is re-created on every render by the hook, so depending on it would refetch in a loop.
	}, []);

	useRepositionOnResize(bodyRef);

	const labels = useMemo(() => (data?.viewer.company.labels.edges ?? []).map(e => e.node), [data]);
	const byId = useMemo(() => new Map(labels.map(label => [label.id, label])), [labels]);
	const groups = useMemo(() => groupsOf(labels, noCategoryLabel), [labels, noCategoryLabel]);
	const ready = !failed && !loading && !!data;

	useEffect(() => {
		if (ready) inputRef.current?.focus();
	}, [ready]);

	const q = query.trim().toLowerCase();
	// The remaining labels: not already selected, and matching the search.
	const shown = groups
		.map(group => ({
			...group,
			labels: group.labels.filter(l => !selected.includes(l.id) && (!q || (l.name ?? '').toLowerCase().includes(q))),
		}))
		.filter(group => group.labels.length);
	const dirty = !sameSet(selected, stored);

	const add = (id: string) => {
		setSelected(current => [...current, id]);
		setQuery('');
		inputRef.current?.focus();
	};
	const remove = (id: string) => setSelected(current => current.filter(x => x !== id));
	const update = () => {
		close();
		if (dirty) Util.CommitMutation(UpdateProjectMutation, {project: {id: node.id}, labels: selected}, onCommit);
	};
	const toggleGroup = (key: string) =>
		setCollapsed(current => (current.includes(key) ? current.filter(k => k !== key) : [...current, key]));

	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Escape') {
			close();
		} else if (event.key === 'Backspace' && !query && selected.length) {
			remove(selected[selected.length - 1]);
		} else if (event.key === 'Enter') {
			event.preventDefault();
			// Enter takes the first match while searching, and saves otherwise.
			if (q && shown.length) add(shown[0].labels[0].id);
			else if (!q) update();
		}
	};

	return (
		<Body ref={bodyRef} data-cy="labels-editor">
			<AboveChatButton />
			<Title>{title}</Title>
			{failed ? (
				<Text data-cy="labels-failed">{failedLabel}</Text>
			) : !ready ? (
				<Text data-cy="labels-loading">{loadingLabel}</Text>
			) : (
				<Picker data-cy="labels-picker">
					<Field onClick={() => inputRef.current?.focus()} data-cy="labels-field">
						{selected.flatMap(id => {
							const label = byId.get(id);
							if (!label) return [];
							const color = label.color || '';
							return [
								<SelectedChip key={id} $color={color} data-cy="labels-selected">
									<WorkSignifier workId={label.name ?? ''} color={color} />
									<RemoveButton
										aria-label={removeLabel(label.name ?? '')}
										data-cy="labels-remove"
										onClick={event => {
											event.stopPropagation();
											remove(id);
										}}
									>
										<Icon icon="closeS" size="s" customColor={getTextColor(color, theme as never)} />
									</RemoveButton>
								</SelectedChip>,
							];
						})}
						<SearchInput
							ref={inputRef}
							value={query}
							onChange={event => setQuery(event.target.value)}
							onKeyDown={onKeyDown}
							aria-label={searchLabel}
							placeholder={selected.length ? '' : searchLabel}
							data-cy="labels-search"
						/>
					</Field>
					<Options data-cy="labels-options">
						{shown.length ? (
							shown.map(group => {
								const open = !collapsed.includes(group.key);
								return (
									<div key={group.key} data-cy="labels-group">
										<GroupHeading aria-expanded={open} onClick={() => toggleGroup(group.key)}>
											<span>{group.name}</span>
											<Icon icon={open ? 'chevronUp' : 'chevronDown'} size="s" />
										</GroupHeading>
										{open && (
											<GroupItems>
												{group.labels.map(label => (
													<OptionChip
														key={label.id}
														data-cy="labels-option"
														onClick={() => add(label.id)}
													>
														<WorkSignifier workId={label.name ?? ''} color={label.color || ''} />
													</OptionChip>
												))}
											</GroupItems>
										)}
									</div>
								);
							})
						) : (
							<Empty data-cy="labels-empty">{q ? noMatchLabel(query.trim()) : allAppliedLabel}</Empty>
						)}
					</Options>
				</Picker>
			)}
			<Footer>
				<FlexRow justifyContent="flex-end" gap="s">
					<Button emphasis={EMPHASIS.LOW} onPress={close} data-cy="labels-cancel">
						{cancelLabel}
					</Button>
					<Button emphasis={EMPHASIS.HIGH} disabled={!dirty} onPress={update} data-cy="labels-update">
						{updateLabel}
					</Button>
				</FlexRow>
			</Footer>
		</Body>
	);
};

export default LabelsEditor;
